import { ReactNode, useState } from "react";

// MUI
import { Box, Button, Collapse, IconButton, Stack, SxProps, Theme, Typography, useTheme } from "@mui/material";

// Components
import { FormattedMessage } from "react-intl";
import CustomIcon from "components/CustomIcon";
import { CircledElement, VerticalLine } from "pages/ProjectDashboard";

// Enums
import icons from "enums/icons";

// Store
import { FilterFieldButton } from "..";
import { TModalFor } from "components/CreateProjectSectionModal/CreateProjectSectionModal";
import ProText from "components/Pro/ProText";

interface DashboardGroupProps {
    data: any;
    number: number;
    title: string;
    description: string;
    isLoading: boolean;
    children: ReactNode;
    filterValue: string;
    showVerticalLine?: boolean;
    hasPermissions?: boolean;
    disabled?: boolean;
    handleCreate?: (type: TModalFor) => Promise<void>;
    handleFilter: React.Dispatch<React.SetStateAction<string>>;
    sx: SxProps<Theme>;
}

const DashboardGroup = ({
    data,
    number,
    title,
    description,
    isLoading,
    children,
    filterValue,
    showVerticalLine = true,
    hasPermissions = true,
    disabled = false,
    handleCreate,
    handleFilter,
    sx,
}: DashboardGroupProps) => {
    const theme = useTheme();
    const [isExpanded, setIsExpanded] = useState(true);

    const noData = data?.length === 0;

    return (
        <Stack direction="row" gap={2} sx={sx}>
            {/* Number */}
            <Box justifyContent="center">
                <CircledElement size={34} fontSize={13} isDisabled={!hasPermissions}>
                    {number}
                </CircledElement>
                {showVerticalLine && <VerticalLine height="calc(100% - 34px)" />}
            </Box>

            <Box sx={{ flex: 1, mb: 3 }}>
                {/* Header */}
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        position: "relative",
                        "&::after": {
                            content: "''",
                            position: "absolute",
                            top: "40%",
                            width: "100%",
                            zIndex: -1,
                            borderBottom: `1px solid ${theme.palette.button.disabledBackground}`,
                        },
                    }}
                >
                    <Stack width="100%" direction="row" justifyContent="space-between">
                        <Stack
                            gap={0.5}
                            direction="row"
                            alignItems="center"
                            sx={{ background: theme.palette.common.white, pr: 1, pl: 2, ml: 4 }}
                        >
                            <Typography
                                variant="h5"
                                sx={{ fontWeight: 600, color: disabled ? "text.disabled" : "text.primary" }}
                            >
                                {title}
                            </Typography>

                            {hasPermissions && data.length > 0 && (
                                <IconButton size="small" onClick={() => setIsExpanded(!isExpanded)}>
                                    <CustomIcon
                                        type="outlined"
                                        icon={isExpanded ? icons.chevronUpThin : icons.chevronDownThin}
                                        sx={{ color: "button.active.from" }}
                                    />
                                </IconButton>
                            )}

                            {!hasPermissions && <ProText sx={{ ml: 1 }} />}
                        </Stack>

                        <Box sx={{ background: theme.palette.common.white, px: 2, mr: 4 }}>
                            {handleCreate && (
                                <Button
                                    variant="contained"
                                    sx={{ fontSize: 13, height: 32, mr: 2, py: 0, px: 2 }}
                                    onClick={() => handleCreate("LD Dashboard")}
                                    disabled={isLoading}
                                    startIcon={<CustomIcon icon={icons.plus} sx={{ fontSize: "14px !important" }} />}
                                >
                                    <FormattedMessage id="project.dashboard.new.dashboard" />
                                </Button>
                            )}

                            {(!noData || hasPermissions) && (
                                <FilterFieldButton
                                    value={filterValue}
                                    setValue={handleFilter}
                                    placeholder="Filter Insights"
                                    disabled={isLoading || noData}
                                />
                            )}
                        </Box>
                    </Stack>
                </Box>

                {/* Body */}
                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                    <Typography variant="body1" sx={{ mb: 2, color: disabled ? "text.disabled" : "text.primary" }}>
                        {description}
                    </Typography>
                    <Box>{children}</Box>
                </Collapse>
            </Box>
        </Stack>
    );
};

export default DashboardGroup;
